import { motion } from 'framer-motion'
import { 
  SparklesIcon,
  CloudIcon,
  ChartBarIcon,
  UserGroupIcon,
  PaintBrushIcon,
  DevicePhoneMobileIcon,
  CalendarDaysIcon,
  CogIcon,
  InboxIcon,
  DocumentTextIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  LightBulbIcon,
  RocketLaunchIcon,
  BoltIcon,
  CurrencyDollarIcon,
  DocumentChartBarIcon
} from '@heroicons/react/24/outline'

interface PremiumFeature {
  id: string
  title: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  status: 'active' | 'coming_soon' | 'beta'
  category: 'ai' | 'storage' | 'branding' | 'analytics' | 'mobile' | 'calendar' | 'automation' | 'support'
}

const premiumFeatures: PremiumFeature[] = [
  {
    id: 'ai-automation',
    title: 'AI-Powered Task Automation',
    description: 'Intelligent task suggestions, automated workflows, and smart scheduling based on your work patterns.',
    icon: SparklesIcon,
    status: 'active',
    category: 'ai'
  },
  {
    id: 'ai-insights',
    title: 'Business Intelligence Dashboard',
    description: 'AI-generated insights about productivity, revenue trends, and optimization recommendations.',
    icon: ChartBarIcon,
    status: 'active',
    category: 'ai'
  },
  {
    id: 'unlimited-storage',
    title: 'File Storage (100GB)',
    description: 'Secure cloud storage for your photos, videos, contracts, and project files with automatic backup.',
    icon: CloudIcon,
    status: 'coming_soon',
    category: 'storage'
  },
  {
    id: 'client-portals',
    title: 'Advanced Client Portals',
    description: 'Branded client galleries, project collaboration spaces, and real-time communication tools.',
    icon: UserGroupIcon,
    status: 'beta',
    category: 'branding'
  },
  {
    id: 'custom-branding',
    title: 'Custom Branding',
    description: 'White-label your client portals, invoices, and communications with your logo and brand colors.',
    icon: PaintBrushIcon,
    status: 'coming_soon',
    category: 'branding'
  },
  {
    id: 'mobile-app',
    title: 'Mobile App Access',
    description: 'Full-featured iOS and Android apps for managing your business on the go.',
    icon: DevicePhoneMobileIcon,
    status: 'coming_soon',
    category: 'mobile'
  },
  {
    id: 'advanced-calendar',
    title: 'Advanced Calendar Features',
    description: 'Smart scheduling, availability management, and integration with Google Calendar and Outlook.',
    icon: CalendarDaysIcon,
    status: 'active',
    category: 'calendar'
  },
  {
    id: 'automated-workflows',
    title: 'Automated Workflows',
    description: 'Create custom automation rules for tasks, emails, and follow-ups based on triggers.',
    icon: CogIcon,
    status: 'active',
    category: 'automation'
  },
  {
    id: 'priority-support',
    title: 'Priority Email Support',
    description: '24/7 priority support with guaranteed response times and dedicated account management.',
    icon: InboxIcon,
    status: 'active',
    category: 'support'
  },
  {
    id: 'advanced-reports',
    title: 'Analytics & Reports',
    description: 'Detailed business analytics, revenue tracking, and custom report generation.',
    icon: DocumentTextIcon,
    status: 'active',
    category: 'analytics'
  },
  {
    id: 'unlimited-invoicing',
    title: 'Unlimited Invoicing & Payments',
    description: 'Create unlimited invoices, accept online payments, and track revenue automatically.',
    icon: CreditCardIcon,
    status: 'active',
    category: 'analytics'
  },
  {
    id: 'data-security',
    title: 'Enhanced Security',
    description: 'Advanced encryption, regular backups, and compliance with industry security standards.',
    icon: ShieldCheckIcon,
    status: 'active',
    category: 'support'
  }
]

interface PremiumFeaturesShowcaseProps {
  isOpen: boolean
  onClose: () => void
}

const PremiumFeaturesShowcase = ({ isOpen, onClose }: PremiumFeaturesShowcaseProps) => {
  const getCategoryIcon = (category: PremiumFeature['category']) => {
    switch (category) {
      case 'ai': return SparklesIcon
      case 'storage': return CloudIcon
      case 'branding': return PaintBrushIcon
      case 'analytics': return ChartBarIcon
      case 'mobile': return DevicePhoneMobileIcon
      case 'calendar': return CalendarDaysIcon
      case 'automation': return CogIcon
      case 'support': return ShieldCheckIcon
      default: return LightBulbIcon
    }
  }

  const categories = Array.from(new Set(premiumFeatures.map(f => f.category)))

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className="relative w-full max-w-6xl max-h-[90vh] glass-mockup rounded-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
              <RocketLaunchIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Premium Features</h2>
              <p className="text-gray-400 text-sm">Unlock the full potential of your creative business</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {/* Success Message */}
          <div className="mb-6 p-4 bg-green-500/10 rounded-xl border border-green-500/30">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                <SparklesIcon className="w-4 h-4 text-green-400" />
              </div>
              <div>
                <p className="text-green-400 font-medium">🎉 Welcome to Professional Plan!</p>
                <p className="text-green-300 text-sm">You now have access to all premium features below.</p>
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Professional Plan Features */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3 p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                <CalendarDaysIcon className="w-6 h-6 text-blue-400" />
                <div>
                  <h4 className="text-white font-medium">Smart Scheduling</h4>
                  <p className="text-gray-400 text-sm">AI-powered availability optimization and booking suggestions</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-4 bg-purple-500/10 rounded-lg border border-purple-500/20">
                <ChartBarIcon className="w-6 h-6 text-purple-400" />
                <div>
                  <h4 className="text-white font-medium">Business Analytics</h4>
                  <p className="text-gray-400 text-sm">Revenue tracking, client insights, and performance metrics</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-4 bg-pink-500/10 rounded-lg border border-pink-500/20">
                <SparklesIcon className="w-6 h-6 text-pink-400" />
                <div>
                  <h4 className="text-white font-medium">AI Business Assistant</h4>
                  <p className="text-gray-400 text-sm">Personalized recommendations for pricing, marketing, and growth</p>
                </div>
              </div>

              <div className="flex items-center space-x-3 p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                <UserGroupIcon className="w-6 h-6 text-green-400" />
                <div>
                  <h4 className="text-white font-medium">Client Management</h4>
                  <p className="text-gray-400 text-sm">Advanced client profiles, communication history, and preferences</p>
                </div>
              </div>
            </div>

            {/* Studio Plan Features */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3 p-4 bg-orange-500/10 rounded-lg border border-orange-500/20">
                <BoltIcon className="w-6 h-6 text-orange-400" />
                <div>
                  <h4 className="text-white font-medium">Automated Workflows</h4>
                  <p className="text-gray-400 text-sm">Streamline bookings, client communication, and follow-ups</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-4 bg-cyan-500/10 rounded-lg border border-cyan-500/20">
                <DocumentTextIcon className="w-6 h-6 text-cyan-400" />
                <div>
                  <h4 className="text-white font-medium">Contract Templates</h4>
                  <p className="text-gray-400 text-sm">Professional contracts with e-signature capabilities</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                <CurrencyDollarIcon className="w-6 h-6 text-yellow-400" />
                <div>
                  <h4 className="text-white font-medium">Advanced Pricing</h4>
                  <p className="text-gray-400 text-sm">Dynamic pricing rules based on demand, season, and client tiers</p>
                </div>
              </div>

              <div className="flex items-center space-x-3 p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                <DocumentChartBarIcon className="w-6 h-6 text-red-400" />
                <div>
                  <h4 className="text-white font-medium">Tax Reporting</h4>
                  <p className="text-gray-400 text-sm">Automated expense tracking and tax-ready financial reports</p>
                </div>
              </div>
            </div>
          </div>

          {/* Feature Categories Summary */}
          <div className="mt-8 p-6 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/30">
            <h3 className="text-white font-semibold mb-4">Feature Categories</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {categories.map(category => {
                const Icon = getCategoryIcon(category)
                const categoryFeatures = premiumFeatures.filter(f => f.category === category)
                const activeCount = categoryFeatures.filter(f => f.status === 'active').length
                
                return (
                  <div key={category} className="text-center">
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mx-auto mb-2">
                      <Icon className="w-5 h-5 text-purple-400" />
                    </div>
                    <p className="text-white text-sm font-medium capitalize">{category.replace('_', ' ')}</p>
                    <p className="text-gray-400 text-xs">{activeCount}/{categoryFeatures.length} active</p>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Call to Action */}
          <div className="mt-6 text-center">
            <p className="text-gray-400 mb-4">
              Ready to explore these features? Start with AI-powered task automation and business insights!
            </p>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:from-purple-600 hover:to-pink-600 transition-all"
            >
              Start Using Premium Features
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default PremiumFeaturesShowcase 